/**
 * Matching an OCR'd submission name against a class roster.
 *
 * This is the one place in the app where a piece of text decides whose grades someone sees, so it is
 * deliberately NOT fuzzy: names either agree exactly after normalization or they do not agree at all.
 * It is used to PROPOSE a binding for the teacher to confirm (see distribute-exam-results.ts) — never to
 * grant access on its own. Once confirmed, the binding is a real foreign key (`Submission.studentID`) and
 * nothing here runs again.
 */

/** Placeholder names the pipeline invents when it cannot read one off the sheet — never a real person.
 * Matched against the ALREADY-NORMALIZED name, so it spells "schuler" without the umlaut: `normalizeName`
 * strips diacritics before this is applied, and a literal "ü" here would therefore never match. */
export const PLACEHOLDER_NAME_RE = /^(student[\s_-]*\d+|schuler(?:in)?[\s_-]*\d+|online-abgabe)$/i;

/**
 * Lowercased, whitespace-collapsed, diacritic-stripped form used to compare two names. Letters are never
 * altered and nothing is truncated, so this stays an exact comparison — it only removes the ways the same
 * name is written differently (case, spacing, "Nachname, Vorname" punctuation, umlaut encoding).
 */
export function normalizeName(value: unknown): string {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[.,;]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** True for a name that cannot identify anybody — empty, or one of the pipeline's placeholders. */
export function isUnusableName(normalized: string): boolean {
  return !normalized || PLACEHOLDER_NAME_RE.test(normalized);
}

/** The two spellings a person's name is accepted in: "first last" and "last first". */
export function nameVariants(firstName: unknown, lastName: unknown): string[] {
  const forward = normalizeName(`${String(firstName ?? '')} ${String(lastName ?? '')}`);
  const reversed = normalizeName(`${String(lastName ?? '')} ${String(firstName ?? '')}`);
  return forward === reversed ? [forward] : [forward, reversed];
}

export interface RosterCandidate {
  _id: string;
  firstName?: string | null;
  lastName?: string | null;
}

/**
 * Every roster member whose name matches `rawName`. Returning ALL of them rather than a best guess is the
 * point: two hits means the name is ambiguous, and the caller must surface that to the teacher instead of
 * picking one.
 */
export function findRosterMatches(rawName: unknown, roster: RosterCandidate[]): RosterCandidate[] {
  const normalized = normalizeName(rawName);
  if (isUnusableName(normalized)) return [];

  return roster.filter(candidate => nameVariants(candidate.firstName, candidate.lastName).includes(normalized));
}
